/*
 * Collection grid — filter & sort, "Show more" (AJAX), grid-density toggle, bands.
 *
 * Everything is delegated off the section root so that swapping inner regions never
 * orphans a handler. Three things fetch HTML and patch the page in place:
 *
 *   • Filter / sort / clear — a single click on any [data-filter-link] (built from
 *     filter_value.url_to_add / url_to_remove), [data-sort-link] or [data-clear-filters].
 *     We fetch the target URL (full page — `shopify theme dev` returns null for this
 *     template's main section over the Section Rendering API, and a full-page fetch is
 *     bulletproof across dev and live), then replace [data-filter-bar],
 *     [data-filter-drawer-body] and [data-collection-results], refresh the (n) badges,
 *     and pushState. Pagination resets to page 1 naturally. The mobile drawer stays
 *     open across the swap (we only replace its body) with its scroll + expanded groups
 *     preserved, so filtering feels instant.
 *
 *   • Show more — fetch the next ?page=N (same full-page path), append the freshly
 *     rendered grid items, copy out the new progress / viewed-X-of-Y / has-next state.
 *
 *   • Grid density — no fetch: toggles data-density on the section + persists the choice.
 *
 * Every option is a real <a>, so with JS off the same links navigate normally.
 * Product-card behaviour (quick-add) is delegated in product-card.js, so appended /
 * swapped cards work with no re-init.
 */
(function () {
  var DENSITY_KEY = 'noted:collection-density';

  /* ── Sub-collection band scroll progress (mirrors header/shop-by-scent). Bands can
        arrive via "Show more" appends or a results swap, and inline <script> in parsed
        markup doesn't run — so we (re-)bind after every mutation; the flag stops doubles. */
  function initBands(root) {
    (root || document).querySelectorAll('[data-sub-collection-band]').forEach(function (band) {
      if (band.dataset.bandInit === 'true') return;
      band.dataset.bandInit = 'true';

      /* Parallax — gentle background drift via object-position (a repaint), NEVER a
         transform: a transform layer is excluded from the backdrop the frosted chips
         sample, killing their blur. Mirrors sections/shop-by-scent.liquid's inline
         drift, but bound here so it survives "Show more" appends. Respects reduced
         motion; guarded by the bandInit flag above so it binds once per band. */
      (function () {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        var img = band.querySelector('.sub-collection-band__bg[data-parallax]');
        if (!img) return;
        var intensity = parseFloat(img.getAttribute('data-parallax-intensity')) || 45;
        var pTicking = false, listening = false;
        function pUpdate() {
          pTicking = false;
          var vh = window.innerHeight;
          var rect = band.getBoundingClientRect();
          // progress: 0 as the band's top enters the bottom of the viewport, 1 as its
          // bottom leaves the top. Centred so the offset is 0 mid-screen.
          var progress = (vh - rect.top) / (vh + rect.height);
          progress = Math.max(0, Math.min(1, progress));
          var offset = (progress - 0.5) * intensity * 2;
          img.style.objectPosition = 'center calc(50% + ' + offset.toFixed(1) + 'px)';
        }
        function pOnScroll() {
          if (!pTicking) { pTicking = true; window.requestAnimationFrame(pUpdate); }
        }
        new IntersectionObserver(function (entries) {
          if (entries[0].isIntersecting) {
            if (!listening) { listening = true; window.addEventListener('scroll', pOnScroll, { passive: true }); }
            pOnScroll();
          } else if (listening) {
            listening = false; window.removeEventListener('scroll', pOnScroll);
          }
        }, { rootMargin: '100px 0px' }).observe(band);
      })();

      var scroller = band.querySelector('.sub-collection-band__scroller');
      var nav = band.querySelector('.sub-collection-band__nav');
      var progress = band.querySelector('.sub-collection-band__progress');
      if (!scroller || !nav || !progress) return;

      function update() {
        var max = scroller.scrollWidth - scroller.clientWidth;
        var overflow = max > 1;
        nav.style.display = overflow ? 'block' : 'none';
        progress.style.width = overflow ? Math.round((scroller.scrollLeft / max) * 100) + '%' : '0%';
      }
      var ticking = false;
      scroller.addEventListener('scroll', function () {
        if (!ticking) { ticking = true; window.requestAnimationFrame(function () { ticking = false; update(); }); }
      }, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ── Sticky filter bar shadow — a 0-height sentinel sits just above the bar; once it
        scrolls past the viewport top the bar is pinned, so we flag .is-stuck (CSS adds the
        drop shadow only then). At rest the bar is flat with no shadow bleeding downward. */
  function initStickyShadow(section) {
    var bar = section.querySelector('.collection-grid__bar');
    if (!bar || bar._cfStickInit || !('IntersectionObserver' in window)) return;
    bar._cfStickInit = true;
    var sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'height:0;margin:0;padding:0;pointer-events:none;';
    bar.parentNode.insertBefore(sentinel, bar);
    new IntersectionObserver(function (entries) {
      bar.classList.toggle('is-stuck', !entries[0].isIntersecting);
    }, { threshold: [0] }).observe(sentinel);
  }

  /* ── Grid density ── */
  function applyDensity(section, mode) {
    section.setAttribute('data-density', mode === 'list' ? 'list' : 'grid');
    section.querySelectorAll('[data-grid-toggle]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', mode === 'list' ? 'true' : 'false');
    });
  }
  function initDensity(section) {
    var saved;
    try { saved = window.localStorage.getItem(DENSITY_KEY); } catch (e) { saved = null; }
    applyDensity(section, saved === 'list' ? 'list' : 'grid');
  }
  function toggleDensity(section) {
    var next = section.getAttribute('data-density') === 'list' ? 'grid' : 'list';
    applyDensity(section, next);
    try { window.localStorage.setItem(DENSITY_KEY, next); } catch (e) {}
    // 2-up ↔ 1-up changes the mobile column count, so re-balance the held trailing row.
    reflowBackfill(section);
  }

  /* ── Desktop facet dropdowns — one open at a time, remembered across swaps. ── */
  function setFacet(section, key) {
    section._cfFacet = key || null;
    section.querySelectorAll('[data-facet-toggle]').forEach(function (btn) {
      var on = key && btn.getAttribute('data-filter-key') === key;
      btn.setAttribute('aria-expanded', on ? 'true' : 'false');
    });
    section.querySelectorAll('[data-facet-panel]').forEach(function (panel) {
      var on = key && panel.getAttribute('data-filter-key') === key;
      panel.classList.toggle('is-open', !!on);
    });
  }
  function reopenFacet(section) {
    var key = section._cfFacet;
    var found = false;
    if (key) {
      section.querySelectorAll('[data-facet-panel]').forEach(function (p) {
        if (p.getAttribute('data-filter-key') === key) found = true;
      });
    }
    setFacet(section, found ? key : null);
  }

  /* ── Mobile drawer (focus trap + inert background, mirrors header drawer) ── */
  function focusables(root) {
    return Array.prototype.filter.call(
      root.querySelectorAll('a[href],button:not([disabled]),input,select,textarea,summary,[tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetWidth || el.offsetHeight || el.getClientRects().length; }
    );
  }
  function backgroundInert(drawer, on) {
    Array.prototype.forEach.call(document.body.children, function (el) {
      if (el === drawer || el.contains(drawer)) return;
      if (on) { el.setAttribute('inert', ''); el.setAttribute('aria-hidden', 'true'); }
      else { el.removeAttribute('inert'); el.removeAttribute('aria-hidden'); }
    });
  }
  function openDrawer(section) {
    var drawer = section.querySelector('[data-filter-drawer]');
    if (!drawer || drawer.classList.contains('is-open')) return;
    if (section._cfCloseTimer) { clearTimeout(section._cfCloseTimer); section._cfCloseTimer = null; }
    section._cfTrigger = section.querySelector('[data-drawer-open]');
    drawer.hidden = false;
    // next frame so the transition runs from the hidden state
    window.requestAnimationFrame(function () {
      drawer.classList.add('is-open');
      document.body.classList.add('noted-drawer-open');
      backgroundInert(drawer, true);
      if (section._cfTrigger) section._cfTrigger.setAttribute('aria-expanded', 'true');
      var f = focusables(drawer.querySelector('.cf-drawer__panel'));
      if (f.length) f[0].focus();
    });
  }
  function closeDrawer(section) {
    var drawer = section.querySelector('[data-filter-drawer]');
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    document.body.classList.remove('noted-drawer-open');
    backgroundInert(drawer, false);
    if (section._cfTrigger) { section._cfTrigger.setAttribute('aria-expanded', 'false'); section._cfTrigger.focus(); }
    section._cfCloseTimer = setTimeout(function () { drawer.hidden = true; }, 320);
  }

  /* ── Drawer state preservation across a body swap ── */
  function captureAccordions(section) {
    var open = {};
    section.querySelectorAll('[data-filter-drawer-body] details[data-acc-key]').forEach(function (d) {
      open[d.getAttribute('data-acc-key')] = d.open;
    });
    return open;
  }
  function restoreAccordions(section, open) {
    if (!open) return;
    section.querySelectorAll('[data-filter-drawer-body] details[data-acc-key]').forEach(function (d) {
      var k = d.getAttribute('data-acc-key');
      if (Object.prototype.hasOwnProperty.call(open, k)) d.open = open[k];
    });
  }

  // Update the (n) badges on the trigger + drawer header. The count source lives on
  // [data-active-total] (the bar / drawer body) — kept on a SEPARATE attribute from the
  // [data-active-count] badge spans so this never writes textContent onto the bar itself
  // (which would wipe its pills).
  function updateActiveCount(section, fromRoot) {
    var src = fromRoot.querySelector('[data-active-total]');
    var n = src ? parseInt(src.getAttribute('data-active-total'), 10) : 0;
    if (isNaN(n)) n = 0;
    section.querySelectorAll('[data-active-count]').forEach(function (el) {
      el.textContent = n > 0 ? '(' + n + ')' : '';
    });
  }

  // Patch a stable container's contents from the fetched markup. We swap innerHTML of
  // a container that is itself never removed (the bar wrap, the drawer scroll area, the
  // results box) rather than replacing the region nodes — so the wrap keeps its
  // responsive classes and open/scroll state, and nothing can leave the DOM mid-swap.
  function swapInner(section, newRoot, selector) {
    var oldEl = section.querySelector(selector);
    var newEl = newRoot.querySelector(selector);
    if (oldEl && newEl) oldEl.innerHTML = newEl.innerHTML;
  }

  function setBusy(section, on) {
    var results = section.querySelector('[data-collection-results]');
    if (results) results.style.opacity = on ? '0.55' : '';
    section._cfLoading = on;
  }

  /* Fetch `url` and patch the bar, drawer body and results in place. */
  function navigate(section, url) {
    if (section._cfLoading) return;
    var scroller = section.querySelector('[data-filter-drawer-scroll]');
    var savedScroll = scroller ? scroller.scrollTop : 0;
    var openAcc = captureAccordions(section);

    var fetchUrl = new URL(url, window.location.origin);
    fetchUrl.searchParams.delete('section_id');
    setBusy(section, true);

    fetch(fetchUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { if (!r.ok) throw new Error('Request failed: ' + r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newSection = doc.querySelector('[data-collection-grid]');
        if (!newSection) throw new Error('No collection grid in response');

        swapInner(section, newSection, '[data-filter-bar-wrap]');
        swapInner(section, newSection, '[data-filter-drawer-scroll]');
        swapInner(section, newSection, '[data-collection-results]');

        updateActiveCount(section, newSection);
        initBands(section);
        reopenFacet(section);
        restoreAccordions(section, openAcc);
        reflowBackfill(section);
        var s2 = section.querySelector('[data-filter-drawer-scroll]');
        if (s2) s2.scrollTop = savedScroll;

        var pushUrl = new URL(url, window.location.origin);
        pushUrl.searchParams.delete('section_id');
        window.history.pushState({ cf: true }, '', pushUrl.toString());
      })
      .catch(function (err) { if (window.console) console.warn('[collection-grid] filter failed:', err); })
      .finally(function () { setBusy(section, false); });
  }

  /* Build a URL on top of the *current* location (preserves active filters) with a
     single param set — used by sort, which Liquid can only render against collection.url. */
  function withParam(name, value) {
    var url = new URL(window.location.href);
    url.searchParams.set(name, value);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    return url.toString();
  }

  /* ── "Show more" backfill ──────────────────────────────────────────────────
     A single fixed page size can't make BOTH the first page (which carries the
     woven promo / review cells) AND the interrupt-free pages after it end on a
     complete desktop row — their row remainders differ by the cell count. Rather
     than strand a ragged half-row of products directly above "Show more", we hold
     that trailing partial row back until the next page (or the final page)
     completes it. Pure presentation: the held <li>s stay in the DOM (just hidden),
     the loaded count is unchanged, and on the LAST page nothing is held so the
     genuine final row shows in full. Re-run after every append / swap / resize. */
  function reflowBackfill(section) {
    var grid = section.querySelector('.js-collection-grid');
    if (!grid) return;

    // Reveal whatever we held last time, so each pass measures from a clean slate.
    Array.prototype.forEach.call(grid.querySelectorAll('[data-backfill-held]'), function (el) {
      el.hidden = false;
      el.removeAttribute('data-backfill-held');
    });

    var region = section.querySelector('[data-load-more-region]');
    if (!region || region.getAttribute('data-has-next') !== 'true') return; // last page: leave the true final row alone.

    var cols = (getComputedStyle(grid).gridTemplateColumns.match(/\S+/g) || []).length;
    if (cols < 2) return; // 1-up list — no partial-row gap exists.

    var items = Array.prototype.filter.call(grid.children, function (el) { return !el.hidden; });
    if (items.length <= cols) return; // only one (possibly partial) row — keep it; hiding it would blank the grid.

    var gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
    var colWidth = (grid.clientWidth - gap * (cols - 1)) / cols;
    if (colWidth <= 0) return;

    // The last visual row = the items sharing the largest offsetTop.
    var maxTop = -Infinity;
    items.forEach(function (el) { if (el.offsetTop > maxTop) maxTop = el.offsetTop; });
    var lastRow = items.filter(function (el) { return Math.abs(el.offsetTop - maxTop) <= 2; });
    if (lastRow.length >= items.length) return; // whole grid is one row — keep it.

    // Column span of that row (a full-width band counts as the whole row).
    var span = 0;
    lastRow.forEach(function (el) { span += Math.max(1, Math.round((el.offsetWidth + gap) / (colWidth + gap))); });
    if (span >= cols) return; // already complete — nothing to hold back.

    lastRow.forEach(function (el) { el.hidden = true; el.setAttribute('data-backfill-held', ''); });
  }

  /* ── Show more ── */
  function loadMore(section) {
    var region = section.querySelector('[data-load-more-region]');
    var grid = section.querySelector('.js-collection-grid');
    var button = section.querySelector('[data-load-more-button]');
    if (!region || !grid || !button) return;
    if (section._cfMoreLoading || region.getAttribute('data-has-next') !== 'true') return;
    section._cfMoreLoading = true;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    var url = new URL(window.location.href);
    url.searchParams.set('page', region.getAttribute('data-next-page'));
    url.searchParams.delete('section_id');

    fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { if (!r.ok) throw new Error('Request failed: ' + r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = doc.querySelector('.js-collection-grid');
        var newRegion = doc.querySelector('[data-load-more-region]');

        if (newGrid) {
          var frag = document.createDocumentFragment();
          while (newGrid.firstElementChild) frag.appendChild(newGrid.firstElementChild);
          grid.appendChild(frag);
          initBands(grid);
        }
        if (newRegion) {
          var shown = newRegion.getAttribute('data-shown');
          var total = newRegion.getAttribute('data-total');
          var hasNext = newRegion.getAttribute('data-has-next');
          region.setAttribute('data-shown', shown);
          region.setAttribute('data-total', total);
          region.setAttribute('data-has-next', hasNext);
          region.setAttribute('data-next-page', newRegion.getAttribute('data-next-page'));

          var countEl = region.querySelector('[data-load-more-count]');
          var freshCount = newRegion.querySelector('[data-load-more-count]');
          if (countEl && freshCount) countEl.textContent = freshCount.textContent;
          var progressEl = region.querySelector('[data-load-more-progress]');
          if (progressEl) {
            var t = parseInt(total, 10), s = parseInt(shown, 10);
            progressEl.style.width = (t > 0 ? Math.round((s / t) * 100) : 100) + '%';
          }
          if (hasNext !== 'true' && button.isConnected) button.remove();
        }
        // Re-balance the trailing row now the new page (and the fresh has-next state) is in.
        reflowBackfill(section);
      })
      .catch(function (err) { if (window.console) console.warn('[collection-grid] show more failed:', err); })
      .finally(function () {
        section._cfMoreLoading = false;
        if (button.isConnected) { button.removeAttribute('aria-busy'); button.disabled = false; }
      });
  }

  /* ── One delegated click handler per section ── */
  function onClick(section, e) {
    var t = e.target;

    var filterLink = t.closest('[data-filter-link]');
    if (filterLink && section.contains(filterLink)) {
      e.preventDefault();
      // Selecting a value closes the desktop dropdown (it does not reopen after the
      // swap); the results + "Your Filters" then update. No-op on mobile (accordions).
      setFacet(section, null);
      navigate(section, filterLink.getAttribute('href'));
      return;
    }
    var clearLink = t.closest('[data-clear-filters]');
    if (clearLink && section.contains(clearLink)) {
      e.preventDefault();
      navigate(section, clearLink.getAttribute('href'));
      return;
    }
    var sortLink = t.closest('[data-sort-link]');
    if (sortLink && section.contains(sortLink)) {
      e.preventDefault();
      setFacet(section, null);
      navigate(section, withParam('sort_by', sortLink.getAttribute('data-sort-value')));
      return;
    }
    var facetToggle = t.closest('[data-facet-toggle]');
    if (facetToggle && section.contains(facetToggle)) {
      e.preventDefault();
      var key = facetToggle.getAttribute('data-filter-key');
      setFacet(section, section._cfFacet === key ? null : key);
      return;
    }
    if (t.closest('[data-drawer-open]') && section.contains(t.closest('[data-drawer-open]'))) {
      e.preventDefault(); openDrawer(section); return;
    }
    if (t.closest('[data-drawer-close]') && section.contains(t.closest('[data-drawer-close]'))) {
      e.preventDefault(); closeDrawer(section); return;
    }
    if (t.closest('[data-grid-toggle]') && section.contains(t.closest('[data-grid-toggle]'))) {
      e.preventDefault(); toggleDensity(section); return;
    }
    if (t.closest('[data-load-more-button]') && section.contains(t.closest('[data-load-more-button]'))) {
      e.preventDefault(); loadMore(section); return;
    }
    if (t.closest('[data-back-to-top]') && section.contains(t.closest('[data-back-to-top]'))) {
      e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); return;
    }
  }

  function initSection(section) {
    if (section.dataset.collectionGridInit === 'true') return;
    section.dataset.collectionGridInit = 'true';

    initBands(section);
    initDensity(section);
    initStickyShadow(section);
    // Hold back the first paint's trailing partial row (no-op when the first page
    // already fills its rows). Wait a frame so the grid has laid out.
    window.requestAnimationFrame(function () { reflowBackfill(section); });

    section.addEventListener('click', function (e) { onClick(section, e); });

    // Fallback native sort <select> (only present when the filter bar is off).
    var legacySort = section.querySelector('[data-collection-sort]');
    if (legacySort) {
      legacySort.addEventListener('change', function () {
        window.location.href = withParam('sort_by', legacySort.value);
      });
    }

    // ESC closes the drawer / an open desktop dropdown.
    section.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      var drawer = section.querySelector('[data-filter-drawer]');
      if (drawer && drawer.classList.contains('is-open')) { closeDrawer(section); return; }
      if (section._cfFacet) setFacet(section, null);
    });

    // Focus trap while the drawer is open.
    section.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var drawer = section.querySelector('[data-filter-drawer]');
      if (!drawer || !drawer.classList.contains('is-open')) return;
      var f = focusables(drawer.querySelector('.cf-drawer__panel'));
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  // Close an open desktop dropdown when clicking outside the bar.
  document.addEventListener('click', function (e) {
    document.querySelectorAll('[data-collection-grid]').forEach(function (section) {
      if (!section._cfFacet) return;
      if (!e.target.closest('[data-filter-bar]')) setFacet(section, null);
    });
  });

  // Column count changes across breakpoints (and with the density toggle), so the
  // held trailing row must be recomputed. Debounced to avoid thrashing on drag-resize.
  var resizeTimer = null;
  window.addEventListener('resize', function () {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      document.querySelectorAll('[data-collection-grid]').forEach(reflowBackfill);
    }, 150);
  });

  function initAll() { document.querySelectorAll('[data-collection-grid]').forEach(initSection); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  document.addEventListener('shopify:section:load', function (event) {
    var section = (event.target.matches && event.target.matches('[data-collection-grid]'))
      ? event.target : event.target.querySelector('[data-collection-grid]');
    if (section) { section.dataset.collectionGridInit = ''; initSection(section); }
  });
})();
